import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  ShoppingCart,
  Tag,
  MessageSquare,
  Heart,
  Settings,
  Store,
  FileText,
  User,
} from "lucide-react";

// Mock user for development
const mockUser = {
  id: "user123",
  name: "John Smith",
  email: "john@example.com",
  role: "buyer", // Can be buyer, seller, or both
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  
  // In a real application, you would get the actual user data from your auth context
  const user = mockUser;
  
  // For this demo, we're checking if user exists
  // In a real app, redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Define tabs based on user role
  const tabs = [
    {
      id: "overview",
      label: "Overview",
      icon: <LayoutDashboard className="h-4 w-4 mr-2" />,
    },
    {
      id: "purchases",
      label: "Purchases",
      icon: <ShoppingCart className="h-4 w-4 mr-2" />,
      showFor: ["buyer", "both"],
    },
    {
      id: "requests",
      label: "My Requests",
      icon: <Tag className="h-4 w-4 mr-2" />,
      showFor: ["buyer", "both"],
    },
    {
      id: "seller",
      label: "Seller Dashboard",
      icon: <Store className="h-4 w-4 mr-2" />,
      showFor: ["seller", "both"],
    },
    {
      id: "messages",
      label: "Messages",
      icon: <MessageSquare className="h-4 w-4 mr-2" />,
    },
    {
      id: "saved",
      label: "Saved Items",
      icon: <Heart className="h-4 w-4 mr-2" />,
    },
    {
      id: "profile",
      label: "Profile",
      icon: <User className="h-4 w-4 mr-2" />,
    },
  ];

  // Filter tabs based on user role
  const userTabs = tabs.filter(
    (tab) => !tab.showFor || tab.showFor.includes(user.role)
  );

  // Redirect to seller dashboard if that tab is selected
  if (activeTab === "seller") {
    navigate("/seller-dashboard");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | BackLink Hub</title>
      </Helmet>
      
      <div className="container py-6 max-w-7xl">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="md:w-64 flex-shrink-0">
            <Card>
              <CardContent className="p-4">
                <div className="space-y-1">
                  {userTabs.map((tab) => (
                    <Button
                      key={tab.id}
                      variant={activeTab === tab.id ? "default" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.icon}
                      {tab.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Main content */}
          <div className="flex-1">
            <TabsContent value="overview" className="mt-0" hidden={activeTab !== "overview"}>
              <DashboardOverview user={user} />
            </TabsContent>
            
            <TabsContent value="purchases" className="mt-0" hidden={activeTab !== "purchases"}>
              <PurchasesTab user={user} />
            </TabsContent>
            
            <TabsContent value="requests" className="mt-0" hidden={activeTab !== "requests"}>
              <RequestsTab user={user} />
            </TabsContent>
            
            <TabsContent value="messages" className="mt-0" hidden={activeTab !== "messages"}>
              <MessagesTab user={user} />
            </TabsContent>
            
            <TabsContent value="saved" className="mt-0" hidden={activeTab !== "saved"}>
              <SavedItemsTab user={user} />
            </TabsContent>
            
            <TabsContent value="profile" className="mt-0" hidden={activeTab !== "profile"}>
              <ProfileTab user={user} />
            </TabsContent>
          </div>
        </div>
      </div>
    </>
  );
};

// Tab content components
const DashboardOverview = ({ user }: { user: any }) => (
  <>
    <h1 className="text-3xl font-bold mb-6">Dashboard</h1>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            <Button size="sm" variant="outline">
              <FileText className="h-4 w-4 mr-2" />
              Create Request
            </Button>
            <Button size="sm" variant="outline">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Browse Offers
            </Button>
            <Button size="sm" variant="outline">
              <MessageSquare className="h-4 w-4 mr-2" />
              Messages
            </Button>
            <Button size="sm" variant="outline">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">You have no recent activity.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">You have no new notifications.</p>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Your recent activity on the platform</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">No recent activity to display.</p>
      </CardContent>
    </Card>
  </>
);

// Mock content for other tabs
const PurchasesTab = ({ user }: { user: any }) => (
  <>
    <h1 className="text-3xl font-bold mb-6">My Purchases</h1>
    <Card>
      <CardHeader>
        <CardTitle>Order History</CardTitle>
        <CardDescription>View and manage your purchases</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">You haven't made any purchases yet.</p>
      </CardContent>
    </Card>
  </>
);

const RequestsTab = ({ user }: { user: any }) => (
  <>
    <h1 className="text-3xl font-bold mb-6">My Requests</h1>
    <Card>
      <CardHeader>
        <CardTitle>Buyer Requests</CardTitle>
        <CardDescription>View and manage your buyer requests</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">You haven't created any requests yet.</p>
      </CardContent>
    </Card>
  </>
);

const MessagesTab = ({ user }: { user: any }) => (
  <>
    <h1 className="text-3xl font-bold mb-6">Messages</h1>
    <Card>
      <CardHeader>
        <CardTitle>Conversations</CardTitle>
        <CardDescription>Your message history with other users</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">You don't have any messages yet.</p>
      </CardContent>
    </Card>
  </>
);

const SavedItemsTab = ({ user }: { user: any }) => (
  <>
    <h1 className="text-3xl font-bold mb-6">Saved Items</h1>
    <Card>
      <CardHeader>
        <CardTitle>Favorites</CardTitle>
        <CardDescription>Offers and sellers you've saved</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">You haven't saved any items yet.</p>
      </CardContent>
    </Card>
  </>
);

const ProfileTab = ({ user }: { user: any }) => {
  const navigate = useNavigate();
  
  return (
    <>
      <h1 className="text-3xl font-bold mb-6">Profile Overview</h1>
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>Your personal and account details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p>
              <strong>Name:</strong> {user.name}
            </p>
            <p>
              <strong>Email:</strong> {user.email}
            </p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/settings")}>
              <Settings className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default Dashboard; 